import { type TypedDocumentNode } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import {
  AiLoader,
  AiSlimBanner,
  Button,
  Css,
  Icon,
  Palette,
  RichTextField,
  useModal,
  useSnackbar,
  useTestIds,
} from "@homebound/beam";
import { useEffect, useRef, useState } from "react";
import { VersionableFragment } from "src/components/ReadyPlanVersionHeader.generated";
import { AggregateVersionStatus } from "src/generated/graphqlTypes";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import {
  GenerateReadyPlanVersionNotesDocument,
  GenerateReadyPlanVersionNotesQuery,
  GenerateReadyPlanVersionNotesQueryVariables,
} from "./GenerateReadyPlanVersionNotes.generated";
import {
  PublishAggregateVersionModal_IncomingSlotFragment,
  usePublishAggregateVersionModal_IncomingSlotsQuery,
  useSaveReadyPlanAggregateVersionMutation,
} from "./PublishAggregateVersionModal.generated";

type PublishAggregateVersionModalProps = {
  copyText: string;
  draftAggregateVersion: VersionableFragment;
  readyPlanName: string;
  readyPlanId?: string;
  /**
   * When true, shows the "Generate with AI" helper which drafts version notes
   * based on detected changes in this draft.
   */
  allowGenerateAiNotes?: boolean;
};

export function PublishAggregateVersionModal(props: PublishAggregateVersionModalProps) {
  const { copyText, draftAggregateVersion, readyPlanName, readyPlanId, allowGenerateAiNotes } = props;
  const { closeModal } = useModal();
  const { triggerNotice } = useSnackbar();
  const [versionNotes, setVersionNotes] = useState<string | undefined>();
  const [hasGenerated, setHasGenerated] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [save] = useSaveReadyPlanAggregateVersionMutation();
  // `client.query` instead of a lazy query hook: a lazy query's execute function reuses one ObservableQuery, so
  // re-running it with the same `readyPlanId` short-circuits to the prior result and "Regenerate" never re-fetches.
  const client = useApolloClient();

  // This component owns the query so the page doesn't have to refetch the slots after every TLI add/update.
  // cache-and-network is needed so a reopen after TLI edits doesn't serve stale slots (which cache-first would).
  const { data: slotsData } = usePublishAggregateVersionModal_IncomingSlotsQuery({
    variables: { id: draftAggregateVersion.id },
    fetchPolicy: "cache-and-network",
  });
  const incomingSlots = slotsData?.readyPlanAggregateVersion.incomingSlotsFulfilledByPublish ?? [];

  const { version } = draftAggregateVersion ?? {};

  // The AI helper needs a readyPlanId to query against, so only offer it when we have one.
  const canGenerateAiNotes = !!allowGenerateAiNotes && !!readyPlanId;

  async function publishPlanPackage() {
    await save({
      variables: { input: { id: draftAggregateVersion.id, status: AggregateVersionStatus.Active, versionNotes } },
    });
    triggerNotice({
      message: (
        <>
          v{version} of <span css={Css.tdu.$}>{readyPlanName}</span> has been published.
        </>
      ),
    });
    closeModal();
  }

  // Kick off the AI draft as soon as the modal opens so the user doesn't have to click "Generate"
  // (the button stays around as a "Regenerate" affordance). The ref guards against double-firing.
  const hasAutoGenerated = useRef(false);
  useEffect(() => {
    if (canGenerateAiNotes && !hasAutoGenerated.current) {
      hasAutoGenerated.current = true;
      void generateNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canGenerateAiNotes]);

  async function generateNotes() {
    if (!readyPlanId) return;
    setGenerating(true);
    try {
      // A lazy query hook can't drive "Regenerate": its execute function reuses one ObservableQuery, so
      // re-running it with the same `readyPlanId` resolves from the last result without hitting the network.
      const { data } = await client.query({
        query: GenerateReadyPlanVersionNotesDocument as TypedDocumentNode<
          GenerateReadyPlanVersionNotesQuery,
          GenerateReadyPlanVersionNotesQueryVariables
        >,
        variables: { readyPlanId },
        fetchPolicy: "no-cache",
      });
      const notes = data?.generateReadyPlanVersionNotes.notes;
      // No notes (empty string) means we couldn't detect any changes worth summarizing, so let the user know.
      if (!notes) {
        triggerNotice({
          icon: "warning",
          message: "No changes were detected to summarize. You can write the notes manually.",
        });
        return;
      }

      setVersionNotes(notes);
      setHasGenerated(true);
    } catch {
      triggerNotice({
        icon: "error",
        message: "Could not generate version notes. Please try again or write them manually.",
      });
    } finally {
      setGenerating(false);
    }
  }

  function clearNotes() {
    setVersionNotes(undefined);
    setHasGenerated(false);
  }

  return (
    <ConfirmationModal
      title={`Ready to Publish v${version}?`}
      label="Publish"
      onConfirmAction={() => publishPlanPackage()}
      // Once AI has drafted notes, label them full-bleed above the body and offer a quick clear back to a blank field.
      banner={
        canGenerateAiNotes && hasGenerated && !generating ? (
          <AiSlimBanner
            title="AI-drafted from detected changes — edit as needed."
            action={{ label: "Clear", onClick: clearNotes }}
          />
        ) : undefined
      }
      confirmationMessage={
        <div css={Css.df.fdc.gap2.$}>
          <p>{copyText}</p>
          {incomingSlots.nonEmpty && <IncomingSlotsSummary slots={incomingSlots} />}
          <VersionNotesField
            value={versionNotes}
            onChange={(val) => setVersionNotes(val)}
            allowGenerateAiNotes={canGenerateAiNotes}
            loading={generating}
            hasGenerated={hasGenerated}
            onGenerate={generateNotes}
          />
        </div>
      }
    />
  );
}

type VersionNotesFieldProps = {
  value: string | undefined;
  onChange: (val: string | undefined) => void;
  allowGenerateAiNotes?: boolean;
  loading: boolean;
  hasGenerated: boolean;
  onGenerate: () => void;
};

function VersionNotesField(props: VersionNotesFieldProps) {
  const { value, onChange, allowGenerateAiNotes, loading, hasGenerated, onGenerate } = props;
  // Keep the field's stable "versionNotes" test id even though we render our own label.
  const tid = useTestIds(props, "versionNotes");
  return (
    <div css={Css.df.fdc.gap1.$}>
      {/* Field label on the left, inline AI generate/regenerate action on the right. */}
      <div css={Css.df.aic.jcsb.$}>
        <div css={Css.df.aic.gap1.$}>
          <span css={Css.sm.gray700.$}>Version Notes</span>
          {loading && <AiLoader inc={2} label="Generating version notes" />}
        </div>
        {allowGenerateAiNotes && (
          <Button
            size="sm"
            variant="ai"
            label={loading ? "Generating…" : hasGenerated ? "Regenerate" : "Generate notes"}
            tooltip={
              hasGenerated
                ? "Replace the notes with a fresh AI summary of the changes detected in this draft."
                : "Summarize the changes detected in this draft into version notes you can edit before publishing."
            }
            disabled={loading}
            onClick={onGenerate}
          />
        )}
      </div>
      <RichTextField
        value={value}
        onChange={(val) => onChange(val)}
        placeholder={loading ? "Reviewing your draft and summarizing changes…" : undefined}
        {...tid}
      />
    </div>
  );
}

type IncomingSlotsSummaryProps = { slots: PublishAggregateVersionModal_IncomingSlotFragment[] };

/**
 * Summarizes the pending incoming slots that publishing will auto-accept, grouped by Design Package,
 * so the user can see at a glance which locations and material variants they're fulfilling.
 */
function IncomingSlotsSummary(props: IncomingSlotsSummaryProps) {
  const { slots } = props;
  const tid = useTestIds(props, "incomingSlots");
  const slotsByDesignPackage = new Map(slots.groupByObject((s) => s.designPackage));
  return (
    <div css={Css.df.fdc.gap1.bgGray100.br8.p2.$} {...tid}>
      <div css={Css.df.aic.gap1.$}>
        <Icon icon="infoCircle" inc={2} color={Palette.Blue600} />
        <span css={Css.smSb.gray800.$}>Publishing will fulfill the following slot requests</span>
      </div>
      <div css={Css.df.fdc.gap2.pl3.$}>
        {[...slotsByDesignPackage].map(([dp, incomingSlots]) => (
          <div key={dp.id} css={Css.df.fdc.gap1.$}>
            <span css={Css.xsSb.gray700.$} {...tid.designPackage}>
              {dp.name}
            </span>
            {incomingSlots.map((slot) => (
              <div key={slot.id} css={Css.df.aic.gap1.xs.$} {...tid.slot}>
                <span>{slot.planPackageRpl.name}</span>
                <span css={Css.gray700.$}>{slot.designPackagePlaceholder.materialVariant!.displayName}</span>
                <span css={Css.gray700.$}>{slot.designPackagePlaceholder.location.displayLocationPath}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
